/* eslint-disable complexity */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { profileBillingApplier, useCart } from '@/data/Content/Cart';
import { checkoutProfilesFetcher } from '@/data/Content/CheckoutProfiles';
import { useNotifications } from '@/data/Content/Notifications';
import { PAYMENTS_IDS } from '@/data/Content/Payment';
import { useCheckoutEvents } from '@/data/Content/_CheckoutEvents';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import {
	getUniqueShippingMethods,
	shippingInfoFetcher,
	shippingInfoUpdateFetcher,
} from '@/data/Content/_ShippingInfo';
import { submitRecurringOrSubscription } from '@/data/Content/_Subscription';
import { getLocalization, useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { useUser } from '@/data/User';
import { BOPIS_STEPS, DELIVERY_STEPS, STEPS } from '@/data/constants/checkout';
import {
	DATA_KEY_CHECKOUT_PROFILES,
	DATA_KEY_PAYMENT_INFO,
	DATA_KEY_SHIPPING_INFO,
} from '@/data/constants/dataKey';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { RECURRING_ORDER_OPTIONS } from '@/data/constants/recurringOrder';
import { useRecurringOrderState } from '@/data/state/useRecurringOrderState';
import { useStoreLocatorState } from '@/data/state/useStoreLocatorState';
import { transactionsPaymentAplazo } from '@/data/transactions';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { ContentProps } from '@/data/types/ContentProps';
import { Order } from '@/data/types/Order';
import { PurchaseOrderContext } from '@/data/types/PurchaseOrder';
import { RequestQuery } from '@/data/types/RequestQuery';
import { checkoutProfileMapper } from '@/data/utils/checkoutProfileMapper';
import { dAdd, dFix } from '@/data/utils/floatingPoint';
import { generateKeyMatcher } from '@/data/utils/generateKeyMatcher';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { cartMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/cartMutatorKeyMatcher';
import { orderHistoryMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/orderHistoryMutatorKeyMatcher';
import { processError } from '@/data/utils/processError';
import { processShippingInfoUpdateError } from '@/data/utils/processShippingInfoUpdateError';
import { transactionsCart } from 'integration/generated/transactions';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { isEmpty, keyBy } from 'lodash';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import useSWR, { mutate } from 'swr';
export type ReviewType = {
	cvv?: string;
};

const paymentAplazo =
	(pub: boolean) =>
	async (
		storeId: string,
		data: Record<string, any>,
		query: RequestQuery = {},
		params: RequestParams = {}
	) =>
		await transactionsPaymentAplazo(pub).cartCheckOutAplazo(storeId, query, data as any, params);

export const preCheckout =
	(pub: boolean) =>
	async (storeId: string, query: RequestQuery = {}, params: RequestParams = {}) =>
		await transactionsCart(pub).cartPreCheckout(storeId, query, undefined, params);

const checkout =
	(pub: boolean) =>
	async (
		storeId: string,
		data: Record<string, any>,
		query: RequestQuery = {},
		params: RequestParams = {}
	) =>
		await transactionsCart(pub).cartCheckOut(storeId, query, data as any, params);

export const getCheckOut = async ({ cache, id: _id, context }: ContentProps) => {
	await Promise.all([
		getLocalization(cache, context.locale || 'en-US', 'Checkout'),
		getLocalization(cache, context.locale || 'en-US', 'MultipleShipmentTable'),
		getLocalization(cache, context.locale || 'en-US', 'Shipping'),
		getLocalization(cache, context.locale || 'en-US', 'AddressForm'),
		getLocalization(cache, context.locale || 'en-US', 'Pickup'),
		getLocalization(cache, context.locale || 'en-US', 'OrderMethod'),
	]);
};

const validateProfileUsage = (profile: string | undefined, order: Order | undefined) => {
	// validate that shipping and payment info is present and tally is correct
	const used = !!(
		profile &&
		order?.orderItem?.every(({ shipModeId }) => shipModeId === order.orderItem[0].shipModeId) &&
		dFix(order?.grandTotal) ===
			dAdd(...(order?.paymentInstruction?.map(({ piAmount }) => piAmount) ?? []))
	);
	return used;
};

export const useCheckOut = () => {
	const { settings } = useSettings();
	const { user } = useUser();
	const { data, loading, error, mutateCart, orderItems, ...rest } = useCart();
	const [activeStep, setActiveStep] = useState(() => 0);
	const {
		recurringOrderInfo,
		actions: { resetRecurringOrderDetails },
	} = useRecurringOrderState();
	const { isRecurring, frequency, startDate } = recurringOrderInfo;

	/**
	 * @deprecated no longer maintained -- DO NOT USE
	 */
	const [waiting, setWaiting] = useState<boolean>(false);

	const router = useNextRouter();
	const { storeId, langId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();
	const routes = useLocalization('Routes');
	const { notifyError } = useNotifications();
	const { storeLocator } = useStoreLocatorState();
	const selectedStoreName = storeLocator?.selectedStore?.storeName;
	const [bopisSelected, setBopisSelected] = useState<boolean>(selectedStoreName ? true : false);
	const steps = useMemo(() => (bopisSelected ? BOPIS_STEPS : DELIVERY_STEPS), [bopisSelected]);
	const { onPurchaseEvent, onCheckoutEvent, onCheckoutPaymentEvent } = useCheckoutEvents({
		cart: data as Order,
		settings,
		activeStep,
		steps,
	});
	const [multiplePayment, setMultiPayment] = useState(1 < (data?.paymentInstruction?.length ?? 0));
	const profileId = useMemo(() => router.query.profile as string, [router.query.profile]);
	const profileUsed = useMemo(() => validateProfileUsage(profileId, data), [profileId, data]);
	const { data: profiles } = useSWR(
		profileUsed && storeId
			? [{ storeId, query: { langId, responseFormat: 'json' } }, DATA_KEY_CHECKOUT_PROFILES]
			: null,
		async ([props]) => checkoutProfilesFetcher(true)(props.storeId, props.query, params)
	);
	const profileList = useMemo(() => checkoutProfileMapper(profiles), [profiles]);
	const toggleMultiplePayment = () => setMultiPayment((previous: boolean) => !previous);
	const { data: usableShipping, mutate: mutateUsableShippingInfo } = useSWR(
		storeId ? [{ storeId, query: { langId } }, DATA_KEY_SHIPPING_INFO] : null,
		async ([{ storeId, query }]) => shippingInfoFetcher(true)(storeId, query, params),
		{ revalidateOnMount: true }
	);
	const [poContext, setPOContext] = useState<PurchaseOrderContext>({ value: '' });
	const poRequired = useMemo(() => data?.x_isPurchaseOrderNumberRequired === 'true', [data]);
	const shipMethods = useMemo(
		() => getUniqueShippingMethods(usableShipping, orderItems),
		[usableShipping, orderItems]
	);

	const toggleBopis = async (_event: React.MouseEvent<HTMLElement>, newValue: boolean) => {
		// check if an actual change was done -- nothing to do otherwise
		if (newValue !== null) {
			setActiveStep(0);
			await resetBopis(bopisSelected);
			setBopisSelected(newValue);
		}
	};

	const back = useCallback(() => {
		if (profileUsed) {
			router.push(routes.Cart.route.t());
		} else {
			setActiveStep((currentStep) => currentStep - 1);
		}
	}, [profileUsed, router, routes]);

	const next = () => {
		setActiveStep((currentStep) => currentStep + 1);
	};

	const resetBopis = async (isCurrentlyBopis: boolean) => {
		try {
			if (isCurrentlyBopis) {
				const shipModeId = shipMethods?.[0]?.shipModeId ?? '';
				const orderItem = orderItems.map(() => ({ shipModeId }));
				const body = { shipModeId, addressId: '', orderItem };
				await shippingInfoUpdateFetcher(settings?.storeId ?? '', {}, body, params);
			}
			await mutateCart();
			await mutate(generateKeyMatcher({ [DATA_KEY_PAYMENT_INFO]: true })(EMPTY_STRING), undefined);
		} catch (error) {
			console.error('Error in resetting bopis info', error);
			notifyError(processShippingInfoUpdateError(error as TransactionErrorResponse));
		}
	};

	const resolveProfilePayments = useCallback(
		async (reviewValues?: ReviewType) => {
			if (profileUsed && !isEmpty(reviewValues)) {
				const asMap = keyBy(profileList, 'xchkout_ProfileId');
				const profile = asMap[profileId];
				const { storeId = '', defaultLanguage: langId = '' } = settings;
				await profileBillingApplier({
					profile,
					order: data,
					storeId,
					langId,
					...reviewValues,
					params,
				});
			}
		},
		[profileUsed, profileList, profileId, settings, data, params]
	);

	const submit = useCallback(
		async (reviewValues?: ReviewType) => {
			if (!waiting) {
				setWaiting(true);
				try {
					await resolveProfilePayments(reviewValues);
					await preCheckout(true)(settings?.storeId as string, undefined, params);
					const purchaseorder_id = poRequired ? poContext.value : undefined;
					const purchaseOrder: Record<string, any> = { purchaseorder_id };
					if (isRecurring) {
						const selectedRecurringOptions = RECURRING_ORDER_OPTIONS.find(
							({ value }) => frequency === value
						);
						const { fulfillmentInterval, fulfillmentIntervalUOM } = selectedRecurringOptions ?? {};
						await submitRecurringOrSubscription(true)(
							settings?.storeId,
							data?.orderId as string,
							{ ...purchaseOrder },
							{
								fulfillmentInterval,
								fulfillmentIntervalUOM,
								startDate,
								...purchaseOrder,
							},
							params
						);
						resetRecurringOrderDetails();
					} else {
						await checkout(true)(
							settings?.storeId as string,
							{
								notifyOrderSubmitted: '1',
								notifyMerchant: '1',
								notifyShopper: '1',
								...purchaseOrder,
							},
							undefined,
							params
						);
					}

					await router.push({
						pathname: routes.OrderConfirmation.route.t(),
						query: { orderId: data?.orderId ?? '' },
					});
					mutate(cartMutatorKeyMatcher(EMPTY_STRING), undefined);
					mutate(orderHistoryMutatorKeyMatcher(EMPTY_STRING), undefined);
				} catch (e) {
					notifyError(processError(e as TransactionErrorResponse));
				}
				setWaiting(false);
				if (data) {
					onPurchaseEvent();
				}
			}
		},
		[
			waiting,
			data,
			resolveProfilePayments,
			settings,
			params,
			poRequired,
			poContext.value,
			router,
			routes.OrderConfirmation.route,
			notifyError,
			onPurchaseEvent,
			isRecurring,
			startDate,
			frequency,
			resetRecurringOrderDetails,
		]
	);

	const onPOChange = useCallback(
		(dirty?: boolean) => (event: ChangeEvent<HTMLInputElement>) => {
			setPOContext((old) => ({
				...old,
				dirty: old.dirty ?? dirty,
				value: event.target.value,
				error: !event.target.value,
			}));
		},
		[]
	);

	const validatePO = useCallback(() => {
		let rc = true;
		if (poRequired) {
			const { value } = poContext;
			rc = !!value.trim();
			onPOChange(true)({ target: { value } } as ChangeEvent<HTMLInputElement>);
		}
		return rc;
	}, [onPOChange, poContext, poRequired]);

	useEffect(() => {
		scrollTo(0, 0);
	}, [activeStep]);

	useEffect(() => {
		onCheckoutEvent();
	}, [activeStep, onCheckoutEvent]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		// refetch payment-info (once)
		mutate(generateKeyMatcher({ [DATA_KEY_PAYMENT_INFO]: true })(EMPTY_STRING), undefined);
	}, []);

	const directTyp = useCallback(async () => {
		await router.push({
			pathname: routes.OrderConfirmation.route.t(),
			query: { orderId: data?.orderId ?? '' },
		});
	}, [data?.orderId, router, routes.OrderConfirmation.route]);

	const submitCustom = useCallback(
		async (
			reviewValues?: ReviewType,
			skipPreCheckout = false,
			checkoutValues?: Record<string, any>
		) => {
			await resolveProfilePayments(reviewValues);
			if (!skipPreCheckout) {
				await preCheckout(true)(settings?.storeId as string, undefined, params);
			}
			const purchaseorder_id = poRequired ? poContext.value : undefined;
			const purchaseOrder: Record<string, any> = { purchaseorder_id };
			if (isRecurring) {
				const selectedRecurringOptions = RECURRING_ORDER_OPTIONS.find(
					({ value }) => frequency === value
				);
				const { fulfillmentInterval, fulfillmentIntervalUOM } = selectedRecurringOptions ?? {};
				await submitRecurringOrSubscription(true)(
					settings?.storeId,
					data?.orderId as string,
					{ ...purchaseOrder },
					{
						fulfillmentInterval,
						fulfillmentIntervalUOM,
						startDate,
						...purchaseOrder,
					},
					params
				);
				resetRecurringOrderDetails();
			} else {
				const isAplazo = data?.paymentInstruction?.at(0)?.payMethodId === PAYMENTS_IDS.APLAZO;

				if (isAplazo) {
					const response = await paymentAplazo(true)(
						settings?.storeId,
						{
							orderId: data?.orderId ?? '1',
							successUrl: `${
								window.location.origin
							}/${routes.OrderConfirmation.route.t()}?orderId=${data?.orderId}`,
							errorUrl: `${window.location.origin}/${routes.CheckOut.route.t()}?errAplazo=1`,
							cartUrl: `${window.location.origin}/${routes.CheckOut.route.t()}`,
						},
						undefined,
						params
					);
					window.location.href = response.data.urlAplazo;
					return;
				}

				await checkout(true)(
					settings?.storeId as string,
					{
						notifyOrderSubmitted: '1',
						notifyMerchant: '1',
						notifyShopper: '1',
						...purchaseOrder,
						...checkoutValues,
					},
					undefined,
					params
				);
			}

			await directTyp();
			mutate(cartMutatorKeyMatcher(EMPTY_STRING), undefined);
			mutate(orderHistoryMutatorKeyMatcher(EMPTY_STRING), undefined);

			if (data) {
				onPurchaseEvent();
			}
		},
		[
			data,
			resolveProfilePayments,
			settings,
			params,
			poRequired,
			poContext.value,
			routes.OrderConfirmation.route,
			onPurchaseEvent,
			isRecurring,
			startDate,
			frequency,
			resetRecurringOrderDetails,
			routes.CheckOut.route,
			directTyp,
		]
	);

	const onSetActiveStep = useCallback(
		(_event: React.SyntheticEvent, newValue: keyof typeof STEPS) => {
			const stepIndex = steps.findIndex((step) => step === newValue);
			setActiveStep(stepIndex);
		},
		[setActiveStep, steps]
	);

	return {
		steps,
		bopisSelected,
		toggleBopis,
		activeStep,
		setActiveStep,
		loading,
		error,
		data,
		mutateCart,
		orderItems,
		waiting,
		submit,
		next,
		back,
		usableShipping,
		mutateUsableShippingInfo,
		multiplePayment,
		toggleMultiplePayment,
		validateProfileUsage,
		profileUsed,
		onPOChange,
		poContext,
		poRequired,
		validatePO,
		isGuest: !user?.isLoggedIn,
		onSetActiveStep,
		submitCustom,
		onCheckoutPaymentEvent,
		directTyp,
		...rest,
	};
};
